import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Wallet, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { useCountdown } from './CountdownText';

const NUDGE_MS = 30 * 60 * 1000;

/** Header pill (free students with wallet credits): balance + live countdown — tap to open checkout with the code applied.
 *  In the last 30 minutes it turns red, pulses and shows a "use them now" bubble. */
export default function WalletPill() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [c, setC] = useState(null);
  const isFree = !user?.subscription_status || user.subscription_status === 'free';
  useEffect(() => {
    if (!isFree) { setC(null); return undefined; }
    let alive = true;
    api.get('/api/credits/me').then((r) => { if (alive) setC(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [isFree, pathname]);
  const cd = useCountdown(c?.active ? c.expires_at : null);
  if (!c || !c.active || cd?.expired) return null;
  const nudge = cd.left <= NUDGE_MS;
  const urgent = nudge || !!cd?.urgent;
  const left = nudge ? `${cd.m}:${String(cd.s).padStart(2, '0')}` : urgent ? cd.text.replace(/^in /, '') : c.expires_text.replace(/^in /, '');
  const at = c.expires_at_text ? ` at ${c.expires_at_text}` : '';
  return (
    <span className="relative inline-flex" data-testid="header-wallet" data-nudge={nudge}>
      <Link to={`/checkout?code=${encodeURIComponent(c.code)}`} title={nudge ? `Use them now — ${c.balance} disappears${at}` : `${c.balance} of wallet credits · expire ${c.expires_text} — only for the Pro pass`}
        className={`inline-flex items-center gap-1.5 rounded-full pl-2 pr-3 py-1.5 text-sm font-bold shadow-sm transition-colors ${nudge ? 'bg-rose-600 text-white hover:bg-rose-700 animate-pulse ring-2 ring-rose-300 ring-offset-1' : urgent ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`} data-testid="header-wallet-pill">
        <span className={`w-5 h-5 rounded-full ${urgent ? 'bg-white text-rose-600' : 'bg-white text-emerald-700'} flex items-center justify-center`}>{nudge ? <Zap size={12} /> : <Wallet size={12} />}</span>
        <span data-testid="header-wallet-balance">{c.balance_short || c.balance}</span>
        <span className="text-[11px] font-semibold opacity-90" data-testid="header-wallet-countdown">· {left} left</span>
      </Link>
      {nudge && (
        <span className="absolute right-0 top-full mt-2 z-40 w-56 rounded-xl bg-rose-600 text-white text-xs font-semibold px-3 py-2 shadow-xl animate-fade-in" data-testid="header-wallet-nudge">
          <span className="absolute -top-1.5 right-6 w-3 h-3 rotate-45 bg-rose-600" />
          Use them now — <b>{c.balance}</b> disappears{at}.
        </span>
      )}
    </span>
  );
}
